/**
 * Step 4 - Tela de validação (dry-run) com progresso em tempo real.
 *
 * A RPC validate_deal_import_job é chamada em chunks (default 500 itens).
 * Para cada chunk processado, o hook useDealImport invoca onProgress com
 * os contadores acumulados — usamos esses dados para mostrar barra de
 * progresso e contagens parciais ao usuário, evitando a impressão de que
 * a tela travou em importações grandes (10k, 20k+).
 *
 * RELEASE:
 *   2026-05-16 v1.1.0 - Adiciona barra de progresso e contagens parciais.
 *   2026-05-16 v1.0.0 - Versão inicial com spinner estático.
 */

import { Loader2, CheckCircle2, AlertCircle, Copy, ShieldAlert } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import type { ValidationCounters } from './types';

interface Step4ValidatingProps {
  progress?: ValidationCounters | null;
  totalRows?: number;
}

export function Step4Validating({ progress, totalRows }: Step4ValidatingProps) {
  // Contadores parciais retornados pelo último chunk da RPC
  const total = progress?.total_rows ?? totalRows ?? 0;
  const valid = progress?.valid_count ?? 0;
  const invalid = progress?.invalid_count ?? 0;
  const duplicate = progress?.duplicate_count ?? 0;
  const blacklisted = progress?.blacklisted_count ?? 0;
  const processed = valid + invalid + duplicate + blacklisted;
  const percent = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  const fmt = (n: number) => n.toLocaleString('pt-BR');

  return (
    <div className="flex flex-col items-center justify-center py-12 gap-6 w-full max-w-xl mx-auto">
      {/* Cabeçalho com spinner */}
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">
            Validando dados no servidor (dry-run)…
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Processando em lotes — não feche esta janela.
          </p>
        </div>
      </div>

      {/* Barra de progresso + contagem total */}
      {total > 0 && (
        <div className="w-full space-y-2">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {fmt(processed)} / {fmt(total)} linhas
            </span>
            <span className="font-medium text-foreground">{percent}%</span>
          </div>
          <Progress value={percent} className="h-2" />
        </div>
      )}

      {/* Contadores parciais por categoria */}
      {processed > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full">
          <MiniStat
            icon={<CheckCircle2 className="h-3.5 w-3.5" />}
            label="Válidos"
            value={fmt(valid)}
            tone="success"
          />
          <MiniStat
            icon={<AlertCircle className="h-3.5 w-3.5" />}
            label="Inválidos"
            value={fmt(invalid)}
            tone="destructive"
          />
          <MiniStat
            icon={<Copy className="h-3.5 w-3.5" />}
            label="Duplicados"
            value={fmt(duplicate)}
            tone="warning"
          />
          <MiniStat
            icon={<ShieldAlert className="h-3.5 w-3.5" />}
            label="Blacklist"
            value={fmt(blacklisted)}
            tone="muted"
          />
        </div>
      )}
    </div>
  );
}

/** Mini-card de contagem parcial com tom semântico. */
function MiniStat({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: 'success' | 'destructive' | 'warning' | 'muted';
}) {
  const toneClass =
    tone === 'success'
      ? 'text-emerald-500'
      : tone === 'destructive'
        ? 'text-destructive'
        : tone === 'warning'
          ? 'text-amber-500'
          : 'text-muted-foreground';
  return (
    <div className="flex flex-col items-center gap-1 rounded-md border border-border bg-muted/30 px-2 py-2">
      <div className={`flex items-center gap-1 text-[11px] ${toneClass}`}>
        {icon}
        <span>{label}</span>
      </div>
      <span className="text-sm font-semibold text-foreground tabular-nums">{value}</span>
    </div>
  );
}
